import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import type { Player, TimedEvent } from "@backstory/player";
import { PANEL_IDS, PANEL_TITLES, deriveMedia, mediaCounts, type PanelActions, type PanelFetchers, type PanelId } from "@backstory/player";
import { PanelHost } from "./PanelHost";
import { StripChevron, useStripOverflow } from "../../components/ui";
import "./devtools.css";

export type DockTab = PanelId | "events" | "why" | "info";
const TABS: DockTab[] = ["events", "why", "info", ...PANEL_IDS.filter((id) => id !== "custom" && id !== "assist"), "custom", "assist"];
const LS_TAB = "backstory-devtools-tab";
const MIN_W = 320;
const MAX_W = 900;
/**
 * Width is deliberately not remembered. Which tab you read is a preference that
 * travels with you; how wide you dragged the dock to see one long URL is a
 * moment, and restoring it on the next session hands you a layout you did not
 * ask for and have to undo.
 */
export const DEFAULT_DOCK_WIDTH = 420;
/** The replay always keeps at least this much room; the dock yields first. */
const MIN_STAGE_W = 360;

export function readDockTab(): DockTab {
  try {
    const v = localStorage.getItem(LS_TAB) as DockTab | null;
    return v && TABS.includes(v) ? v : "events";
  } catch {
    return "events";
  }
}

export interface DevToolsDockProps {
  player: Player | null;
  time: number;
  version: number;
  actions: PanelActions;
  fetchers?: PanelFetchers;
  /** Rendered for the "Events" tab (the existing event list). */
  eventsPanel: ReactNode;
  /** Rendered for the "Why" tab (AI: summary, explain this moment, repro). */
  whyPanel?: ReactNode;
  /** Rendered for the authenticated session metadata tab. */
  infoPanel?: ReactNode;
  /** Badge counts per tab (e.g. issues, console errors). */
  badges?: Partial<Record<DockTab, number>>;
  onWidthChange?: (w: number) => void;
  /** Use the host's full row instead of the persisted side-dock width. */
  fullWidth?: boolean;
  /** Live Assist: panels only hold the window since the agent attached. */
  live?: boolean;
  /** Live Assist: events from before the agent attached, read back from storage. */
  priorEvents?: readonly TimedEvent[];
  /** Approximate place shown on the Application overview. */
  location?: string;
  /** Optional controlled tab, used by session-level health shortcuts. */
  activeTab?: DockTab;
  onTabChange?: (tab: DockTab) => void;
}

export function DevToolsDock({ player, time, version, actions, fetchers, eventsPanel, whyPanel, infoPanel, badges, onWidthChange, fullWidth = false, live = false, priorEvents, activeTab, onTabChange, location }: DevToolsDockProps) {
  const [storedTab, setStoredTab] = useState<DockTab>(readDockTab);
  const assistCount = useMemo(() => {
    if (badges?.assist != null) return badges.assist;
    if (!player) return 0;
    const seen = new Set(player.timeline.events.map((row) => `${row.seq}:${row.idx}`));
    const rows = priorEvents?.length
      ? [...priorEvents.filter((row) => !seen.has(`${row.seq}:${row.idx}`)), ...player.timeline.events]
      : player.timeline.events;
    const annotationIds = new Set<string>();
    let count = 0;
    for (const row of rows) {
      if (row.event.type !== "assist") continue;
      const id = row.event.kind === "annotation" ? row.event.payload?.id : undefined;
      if (id) annotationIds.add(String(id));
      else count++;
    }
    return count + annotationIds.size;
  }, [player, version, priorEvents, badges?.assist]);
  // A tab whose panel the host did not supply is not offered. Showing it and
  // then explaining the emptiness spends a click to say "nothing here".
  const tabs = useMemo(
    () =>
      TABS.filter((candidate) => {
        if (candidate === "assist") return assistCount > 0;
        if (candidate === "why") return !!whyPanel;
        if (candidate === "info") return !!infoPanel;
        return true;
      }),
    [assistCount, whyPanel, infoPanel],
  );
  const requestedTab = activeTab ?? storedTab;
  const tab = tabs.includes(requestedTab) ? requestedTab : "events";
  const setTab = useCallback((next: DockTab) => {
    setStoredTab(next);
    onTabChange?.(next);
  }, [onTabChange]);
  const [width, setWidth] = useState<number>(DEFAULT_DOCK_WIDTH);
  const dragging = useRef<{ startX: number; startW: number } | null>(null);
  const root = useRef<HTMLElement>(null);
  const { ref: tabsRef, more: tabOverflow, nudge: nudgeTabs, center: centerTab } = useStripOverflow<HTMLDivElement>([tabs]);
  /** Widest the dock may be here; a width dragged in a larger window is clamped to it. */
  const [available, setAvailable] = useState(MAX_W);

  useEffect(() => {
    try {
      localStorage.setItem(LS_TAB, tab);
    } catch {
      /* ignore */
    }
  }, [tab]);
  // Measure the workspace, not the dock: the dock's own width is what we are
  // deciding, so reading it back would feed the resize observer its own output.
  // A dock that shares its row leaves room for the replay; one that owns the
  // row (the live console stacks it under the video) may use the full width.
  useEffect(() => {
    const host = root.current?.parentElement;
    if (!host || typeof ResizeObserver === "undefined") return;
    const measure = () => {
      if (fullWidth) {
        setAvailable(Math.max(1, host.clientWidth));
        return;
      }
      const shared = host.children.length > 1;
      setAvailable(Math.max(MIN_W, Math.min(MAX_W, host.clientWidth - (shared ? MIN_STAGE_W : 0))));
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(host);
    return () => ro.disconnect();
  }, [fullWidth]);

  // Centring the active tab by hand rather than scrollIntoView: the strip lives
  // inside a scrollable page, and scrollIntoView moves every ancestor too.
  useEffect(() => {
    centerTab(tabsRef.current?.querySelector<HTMLElement>(`[data-testid="devtools-tab-${tab}"]`) ?? null);
  }, [tab, centerTab, tabsRef]);

  /** Width actually applied: the preference, never wider than the room for it. */
  const applied = fullWidth ? available : Math.max(MIN_W, Math.min(width, available));
  useEffect(() => {
    onWidthChange?.(applied);
  }, [applied, onWidthChange]);

  const onPointerDown = useCallback((e: React.PointerEvent) => {
    dragging.current = { startX: e.clientX, startW: applied };
    (e.target as HTMLElement).setPointerCapture(e.pointerId);
  }, [applied]);
  const onPointerMove = useCallback((e: React.PointerEvent) => {
    if (!dragging.current) return;
    const next = Math.min(MAX_W, Math.max(MIN_W, dragging.current.startW - (e.clientX - dragging.current.startX)));
    setWidth(next);
  }, []);
  const onPointerUp = useCallback(() => {
    dragging.current = null;
  }, []);

  const onKeyDown = (e: React.KeyboardEvent) => {
    const i = tabs.indexOf(tab);
    if (e.key === "ArrowRight") {
      e.preventDefault();
      setTab(tabs[(i + 1) % tabs.length]!);
    } else if (e.key === "ArrowLeft") {
      e.preventDefault();
      setTab(tabs[(i - 1 + tabs.length) % tabs.length]!);
    }
  };

  // The Media badge is derived here rather than passed in, so any host that
  // mounts the dock gets it: call count, or the failure count when a call broke.
  const mediaBadge = useMemo(() => {
    if (badges?.media != null) return badges.media;
    if (!player) return undefined;
    const tl = player.timeline;
    if (!tl.events.length) return undefined;
    const c = mediaCounts(deriveMedia({ events: tl.events, startTs: tl.startTs, now: tl.startTs + time, endTs: Number.isFinite(tl.endTs) ? tl.endTs : tl.startTs }));
    const problems = c.failed + c.mediaErrors + c.freezes;
    return problems || c.calls || undefined;
    // `version` re-derives when new chunks land; `time` is intentionally not a
    // dependency because the badge counts the whole segment, not the playhead.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [player, version, badges?.media]);

  const customBadge = useMemo(() => {
    if (badges?.custom != null) return badges.custom;
    if (!player) return undefined;
    const seen = new Set(player.timeline.events.map((row) => `${row.seq}:${row.idx}`));
    const rows = priorEvents?.length
      ? [...priorEvents.filter((row) => !seen.has(`${row.seq}:${row.idx}`)), ...player.timeline.events]
      : player.timeline.events;
    const count = rows.filter((row) => row.event.type === "custom" && !row.event.name.startsWith("backstory.")).length;
    return count || undefined;
  }, [player, version, priorEvents, badges?.custom]);

  const badgeFor = (t: DockTab): number | undefined => (t === "media" ? mediaBadge : t === "custom" ? customBadge : t === "assist" ? assistCount : badges?.[t]);
  const scrollable = tabOverflow.start || tabOverflow.end;

  return (
    <aside ref={root} className="devtools card" style={{ width: fullWidth ? "100%" : applied, ["--devtools-w" as string]: `${applied}px` }} aria-label="DevTools">
      {!fullWidth && <div
        className="devtools-resize"
        role="separator"
        aria-orientation="vertical"
        aria-label="Resize DevTools"
        aria-valuemin={MIN_W}
        aria-valuemax={MAX_W}
        aria-valuenow={applied}
        tabIndex={0}
        title="Drag to resize (or use ← → when focused)"
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onKeyDown={(e) => {
          if (e.key === "ArrowLeft") setWidth((w) => Math.min(MAX_W, w + 20));
          else if (e.key === "ArrowRight") setWidth((w) => Math.max(MIN_W, w - 20));
        }}
      />}
      <div className="devtools-tabbar">
        {/* Both chevrons stay mounted while the strip overflows, and sit beside
            the tabs instead of over them: an arrow that covers the first tab
            hides the thing it is meant to help you reach. */}
        {scrollable ? <StripChevron back className="devtools-tab-scroll" label="Scroll tabs" disabled={!tabOverflow.start} onClick={() => nudgeTabs(-1)} /> : null}
        <div ref={tabsRef} className="devtools-tabs" role="tablist" aria-label="DevTools panels" onKeyDown={onKeyDown}>
          {tabs.map((t) => (
            <button key={t} role="tab" id={`devtools-tab-${t}`} aria-selected={tab === t} aria-controls={`devtools-panel-${t}`} tabIndex={tab === t ? 0 : -1} onClick={() => setTab(t)} data-testid={`devtools-tab-${t}`}>
              {t === "events" ? "Events" : t === "why" ? <><span className="badge ai" style={{ fontSize: 10, padding: "1px 6px" }}>AI</span> Why</> : t === "info" ? "Session" : PANEL_TITLES[t]}
              {badgeFor(t) ? <span className={`badge-count${t === "assist" ? " neutral" : ""}`}>{badgeFor(t)}</span> : null}
            </button>
          ))}
        </div>
        {scrollable ? <StripChevron className="devtools-tab-scroll" label="Scroll tabs" disabled={!tabOverflow.end} onClick={() => nudgeTabs(1)} /> : null}
      </div>
      <div className="devtools-panel" role="tabpanel" id={`devtools-panel-${tab}`} aria-labelledby={`devtools-tab-${tab}`}>
        {tab === "events" ? eventsPanel : tab === "why" ? whyPanel : tab === "info" ? infoPanel : <PanelHost id={tab} player={player} time={time} version={version} actions={actions} fetchers={fetchers} live={live} priorEvents={priorEvents} location={location} />}
      </div>
    </aside>
  );
}
